// src/components/public/StoryViewer.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Typography,
  Avatar
} from '@mui/material';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import myAGTService from '../../services/myAGTService';

const StoryViewer = ({ stories, open, onClose, professionalName, professionalImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const progressInterval = useRef(null);
  const storyDuration = 5000; // 5 segundos por historia
  const updateInterval = 100; // Actualizar cada 100ms
  
  // Efecto para manejar el progreso y avanzar automáticamente
  useEffect(() => {
    if (!open || stories.length === 0) return;
    
    // Registrar vista
    const registerView = async () => {
      try {
        await myAGTService.viewStory(stories[currentIndex].storyId);
      } catch (error) {
        console.error('Error al registrar vista:', error);
      }
    };
    
    registerView();
    
    // Resetear progreso
    setProgress(0);
    
    // Configurar intervalo para actualizar la barra de progreso
    progressInterval.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (updateInterval / storyDuration) * 100;
        
        // Si llegamos al 100%, avanzar a la siguiente historia
        if (newProgress >= 100) {
          clearInterval(progressInterval.current);
          
          // Avanzar a la siguiente historia si hay más
          if (currentIndex < stories.length - 1) {
            setTimeout(() => {
              setCurrentIndex(prev => prev + 1);
            }, 50);
          } else {
            // Si es la última historia, cerrar después de completar
            setTimeout(() => {
              onClose();
            }, 300);
          }
        }
        
        return newProgress < 100 ? newProgress : 100;
      });
    }, updateInterval);
    
    // Limpiar intervalo al desmontar
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [open, currentIndex, stories, onClose]);
  
  // Manejar navegación entre historias
  const handleNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onClose();
    }
  };
  
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  // Si no hay historias, no mostrar nada
  if (!stories || stories.length === 0) return null;
  
  const currentStory = stories[currentIndex];
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'black',
          overflow: 'hidden',
          maxHeight: '90vh',
          m: 1
        }
      }}
    >
      <Box sx={{ position: 'relative', height: '90vh', maxHeight: '90vh' }}>
        {/* Barras de progreso */}
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5, 
          position: 'absolute', 
          top: 10, 
          left: 10, 
          right: 10, 
          zIndex: 2 
        }}>
          {stories.map((_, index) => (
            <LinearProgress
              key={index}
              variant="determinate"
              value={index < currentIndex ? 100 : index === currentIndex ? progress : 0}
              sx={{
                height: 2,
                flex: 1,
                bgcolor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white'
                }
              }}
            />
          ))}
        </Box>
        
        {/* Información del profesional */}
        <Box sx={{ 
          position: 'absolute', 
          top: 20, 
          left: 10, 
          display: 'flex', 
          alignItems: 'center',
          zIndex: 2
        }}>
          <Avatar 
            src={professionalImage} 
            alt={professionalName}
            sx={{ width: 40, height: 40, mr: 1 }}
          />
          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 500 }}>
            {professionalName}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255,255,255,0.7)', 
              ml: 1,
              fontSize: '0.7rem'
            }}
          >
            {new Date(currentStory.createdAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        </Box>
        
        {/* Botón para cerrar */}
        <IconButton
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            top: 10, 
            right: 10, 
            color: 'white',
            zIndex: 2
          }}
        >
          <X size={24} />
        </IconButton>
        
        {/* Imagen de la historia */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'black'
          }}
        >
          <motion.img
            key={currentStory.storyId}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            src={currentStory.imageUrl}
            alt="Story"
            style={{
              maxHeight: '100%',
              maxWidth: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>
        
        {/* Caption de la historia */}
        {currentStory.caption && (
          <Box sx={{ 
            position: 'absolute', 
            bottom: 20, 
            left: 0, 
            right: 0, 
            p: 2,
            textAlign: 'center'
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'white', 
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                fontWeight: 500,
                px: 2
              }}
            >
              {currentStory.caption}
            </Typography>
          </Box>
        )}
        
        {/* Áreas para navegar entre historias */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '50%',
            height: '100%',
            cursor: 'pointer',
            zIndex: 1
          }}
          onClick={handlePrevious}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            cursor: 'pointer',
            zIndex: 1
          }}
          onClick={handleNext}
        />
        
        {/* Botones de navegación visibles en hover */}
        {currentIndex > 0 && (
          <IconButton
            sx={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.5)'
              },
              zIndex: 2
            }}
            onClick={handlePrevious}
          >
            <ChevronLeft />
          </IconButton>
        )}
        
        {currentIndex < stories.length - 1 && (
          <IconButton
            sx={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.5)'
              },
              zIndex: 2
            }}
            onClick={handleNext}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    </Dialog>
  );
};

export default StoryViewer;